import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { NotImplementedError } from "../../utils/NotImplementedError";
import { baseUrl, fetcher } from "../../data";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import { ImageRecord } from "../image/ImageRecord";

import type { components } from "../../data/openapi-types";
import { isDefined } from "../../utils/typedLoDashUtils";
import { ImageDTO } from "./ImageDTO";
import { assertIsDefined } from "../../utils/assertions";
import { convertIdFilter } from "../../utils/filterUtils";
import { FetchArgType } from "openapi-typescript-fetch";

export type ImageAPIResponse = components["schemas"]["MpmApiResponseImageDTO"];

interface ImageUploadInfo {
    rawFile: File;
    type: "thumbnail" | "banner";
    imageId?: number;
    targetType: "character" | "product" | "serie";
    targetUuid: string;
}

const findAll = fetcher.path("/api/media/image/findAll").method("post").create();
const getOne = fetcher.path("/api/media/image/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/media/image/{uuid}").method("put").create();
const deleteOne = fetcher.path("/api/media/image/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/media/image/allByUuids").method("get").create();
const deleteAllByUuids = fetcher.path("/api/media/image/allByUuids").method("delete").create({ uuids: true });

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns ImageRecord
 */

function mapDTOToRecord(dto: ImageDTO): ImageRecord {
    // DTO from backend
    const { id = 0, uuid = "", url = "", type = "banner", targetType = "character", targetUuid = "" } = dto;

    // Record for front-end
    return {
        shortId: id,
        id: uuid,
        url,
        type,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        targetType,
        targetId: targetUuid
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns ImageDTO
 */

function mapRecordToDTO(record: ImageRecord): ImageDTO {
    // Record from front-end
    const { shortId = 0, id = "", url = "", type = "banner", targetType = "character", targetId = "" } = record;

    // DTO for back-end
    return {
        id: shortId,
        uuid: id as string,
        url,
        type,
        targetType,
        targetUuid: targetId
    };
}

async function uploadImage(uploadInfo: ImageUploadInfo): Promise<ImageAPIResponse> {
    const { rawFile, type, imageId, targetUuid, targetType } = uploadInfo;
    const formData = new FormData();
    formData.append("file", rawFile);

    let url = `${baseUrl}/api/media/image/uploadMedia?type=${type}`;
    url += `&targetType=${targetType}`;
    url += `&targetUuid=${targetUuid}`;

    if (isDefined(imageId)) {
        url += `&imageId=${imageId}`;
    }

    const response = await fetch(url, {
        method: "POST",
        body: formData,
        credentials: "include"
    });

    return (await response.json()) as ImageAPIResponse;
}

interface ImageFilters {
    targetType: string;
    targetId: string;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<ImageRecord>> {
    const { ...filters } = params.filter as ImageFilters;

    if ((params as any).target === "targetId") {
        filters["targetId"] = (params as any).id;
    }

    const parameters: FetchArgType<typeof findAll> = {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        filter: convertIdFilter(filters),
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    const { data } = await findAll(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function _getOne(params: LangParams | (number | string)): Promise<ImageRecord> {
    const {
        data: { content: characterDTO }
    } = await getOne({
        uuid: params as string
    });
    assertIsDefined(characterDTO);

    return mapDTOToRecord({ id: characterDTO.id ?? 0, ...characterDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<ImageRecord>> {
    const {
        data: { content: characterDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(characterDTO);

    return { data: await Promise.all(characterDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<ImageRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<ImageRecord>>): Promise<UpdateResult<ImageRecord>> {
    const payload = mapRecordToDTO(params.data as ImageRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const imageDTO = data.content;
    assertIsDefined(imageDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...imageDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<ImageRecord>> {
    const data = await uploadImage({
        targetType: params.data["targetType"],
        targetUuid: params.data["targetId"],
        type: params.data["type"],
        rawFile: params.data["image"]["rawFile"]
    });

    const imageDTO = data.content;

    assertIsDefined(imageDTO);

    return { data: mapDTOToRecord({ id: imageDTO.id as number, ...imageDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<ImageRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as ImageRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await deleteAllByUuids({
        uuids: params.ids as string[]
    });
    return { data: params.ids as string[] };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<ImageRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const ImageDataProvider: MpmDataProvider<ImageRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne: _getOne,
    update,
    updateMany
};
